import { GanttLinks } from '@/ship/Enums/GanttEnum';
import { PMStructureLinkLocationEnum } from '@/ship/Enums/ProjectManagementEnums';
import { IPMChart } from '@/ship/Models/IProjectManagement';
import { Structure, StructureEditModelConnectionsLink } from '@/ship/Models/ProjectManagement';
import { deleteLinkStructure } from '@/utils/api/ProjectManagement/PMChart';
import ErrorService from '@/utils/ErrorService';

export class LinkManager {
    private _state: StructureEditModelConnectionsLink;
    private _hasBeenDeleted: boolean = false;
    private _hasUpdated: boolean = false;
    private _errorMessages: string[] = [];

    constructor(
        link: StructureEditModelConnectionsLink,
        private defaultChartId: number,
        private request: any,
        private _targetTask?: Structure,
        private _targetChart?: IPMChart,
    ) {
        this._state = {
            ...this.getDefaultState(),
            ...link,
        };
    }

    private getDefaultState(): Partial<StructureEditModelConnectionsLink> {
        return {
            connectionType: GanttLinks.EndStart,
            location: PMStructureLinkLocationEnum.ThisChart,
            delay: null,
            isLoading: false,
            taskError: '',
            connectionTypeError: '',
        };
    }

    get id(): number | null {
        return this._state.id;
    }
    get chartId(): number | null {
        return this._state.chartId;
    }
    get connectionType(): GanttLinks | null {
        return this._state.connectionType;
    }
    get delay(): number | null {
        return this._state.delay;
    }
    get location(): PMStructureLinkLocationEnum {
        return this._state.location;
    }
    get selectedTaskId(): number | null {
        return this._state.selectedTaskId;
    }
    get isLoading(): boolean {
        return this._state.isLoading;
    }
    get taskError(): string {
        return this._state.taskError;
    }
    get connectionTypeError(): string {
        return this._state.connectionTypeError;
    }
    get isNewLink(): boolean {
        return this._state.isNewLink || false;
    }

    // Получение полного состояния (только для чтения)
    get state(): Readonly<StructureEditModelConnectionsLink> {
        return { ...this._state };
    }

    get hasUpdated() {
        return this._hasUpdated;
    }

    get hasBeenDeleted() {
        return this._hasBeenDeleted;
    }

    set hasBeenDeleted(value) {
        this._hasBeenDeleted = value;
    }

    get hasErrors() {
        return !!this.errorMessages.length;
    }

    get errorMessages() {
        return this._errorMessages;
    }

    get targetTask() {
        return this._targetTask;
    }

    setTargetTask(task: Structure) {
        this._targetTask = task;
    }

    removeTargetTask() {
        this._targetTask = undefined;
    }

    get targetChart() {
        return this._targetChart;
    }

    setTargetChart(chart: IPMChart) {
        this._targetChart = chart;
    }

    removeTargetChart() {
        this._targetChart = undefined;
    }

    addError(error: string) {
        this._errorMessages.push(error);
    }

    clearErrors() {
        this._errorMessages = [];
    }

    // Сеттеры для контролируемого изменения состояния
    setChartId(value: number | null): void {
        this._state.chartId = value;
        this._state.selectedTaskId = null;
        this.removeTargetTask();
        this.setUpdatedState();
    }
    setConnectionType(value: GanttLinks | null): void {
        this._state.connectionType = value;
    }
    setDelay(value: number | null): void {
        this._state.delay = value;
        this.setUpdatedState();
    }
    setLocation(value: PMStructureLinkLocationEnum): void {
        this._state.selectedTaskId = null;
        this.removeTargetTask();
        this.setChartId(null);

        if (value === PMStructureLinkLocationEnum.ThisChart) this.setChartId(this.defaultChartId);

        this._state.location = value;
        this.setUpdatedState();
    }
    setSelectedTaskId(value: number | null): void {
        this._state.selectedTaskId = value;
        this.setUpdatedState();
    }
    setIsLoading(value: boolean): void {
        this._state.isLoading = value;
        this.setUpdatedState();
    }

    setConnectionTypeError(value: string): void {
        this._state.connectionTypeError = value;
        this.setUpdatedState();
    }

    setUpdatedState(value: boolean = true) {
        this.clearErrors();
        this._hasUpdated = value;
    }

    reset(): void {
        Object.assign(this._state, this.getDefaultState());
        this._state.selectedTaskId = null;
        this._state.chartId = null;
    }

    update(patch: Partial<StructureEditModelConnectionsLink>): void {
        Object.assign(this._state, patch);
    }

    async handleDeletingLink(): Promise<void> {
        if (!this.id) return;

        try {
            await deleteLinkStructure(this.id);
        } catch (err) {
            ErrorService.handleApiError(err);
        }
    }

    async handleRequest(sourceTaskId: number): Promise<void> {
        this.errorChecker();

        if (this.hasErrors) throw new Error();

        this.setIsLoading(true);
        try {
            await this.request({
                type: this._state.connectionType,
                sourceTaskId: sourceTaskId,
                targetTaskId: this._state.selectedTaskId,
                delay: this._state.delay || undefined,
            });
        } finally {
            this.setIsLoading(false);
        }
    }

    /**
     * @description check if the all links has type and parent task. Setup description of error to all links
     */
    errorChecker() {
        this.clearErrors();
        if (!this._state.selectedTaskId) this.addError('Выберите задачу');
        if (!this.chartId) this.addError('Выберите график');
    }
}
